import { useEffect, useState } from 'react';
import { useAuthStore } from '../store/authStore';
import * as api from '../api';
import type { DailyResponse } from '../types';

const CACHE_KEY = 'daily_challenges';
const OFFLINE_SOLVES_KEY = 'offline_solves';

interface OfflineSolve {
  problemId: string;
  coord: string;
  timeMs: number;
  timestamp: number;
}

export function useDailyChallenges() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [daily, setDaily] = useState<DailyResponse | null>(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached) as DailyResponse;
        const today = new Date().toISOString().split('T')[0];
        if (parsed.date === today) return parsed;
      }
    } catch {
      // ignore
    }
    return null;
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const retry = () => {
    if (!isAuthenticated) return;
    setIsLoading(true);
    setError(null);
    api.getDailyProblems()
      .then((data) => {
        setDaily(data);
        localStorage.setItem(CACHE_KEY, JSON.stringify(data));
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to load');
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    setIsLoading(true);
    setError(null);
    api.getDailyProblems()
      .then((data) => {
        setDaily(data);
        localStorage.setItem(CACHE_KEY, JSON.stringify(data));
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to load');
      })
      .finally(() => setIsLoading(false));
  }, [isAuthenticated]);

  // Sync offline solves when back online
  useEffect(() => {
    const sync = async () => {
      try {
        const raw = localStorage.getItem(OFFLINE_SOLVES_KEY);
        if (!raw) return;
        const solves: OfflineSolve[] = JSON.parse(raw);
        if (solves.length === 0) return;

        for (const solve of solves) {
          try {
            await api.solveProblem(solve.problemId, solve.coord, solve.timeMs);
          } catch {
            // skip failed syncs
          }
        }
        localStorage.removeItem(OFFLINE_SOLVES_KEY);
      } catch {
        // ignore
      }
    };

    window.addEventListener('online', sync);
    if (navigator.onLine) sync();

    return () => window.removeEventListener('online', sync);
  }, []);

  return { daily, isLoading, error, retry };
}

export function saveOfflineSolve(problemId: string, coord: string, timeMs: number) {
  try {
    const raw = localStorage.getItem(OFFLINE_SOLVES_KEY);
    const solves: OfflineSolve[] = raw ? JSON.parse(raw) : [];
    solves.push({ problemId, coord, timeMs, timestamp: Date.now() });
    localStorage.setItem(OFFLINE_SOLVES_KEY, JSON.stringify(solves));
  } catch {
    // ignore
  }
}
